import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Plus, Users } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { isAdmin } from "../../utils/permissions";
import { apiErrorMessage, boardTaskService, sprintService, squadService } from "../../api/taskBoardService";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import { SquadFormModal } from "../../components/task-board/CatalogFormModals";
import { EmptyState, ErrorState, SkeletonRows, formatDate } from "../../components/task-board/shared";
import StatusBar from "../../components/task-board/StatusBar";
import { squadOverview } from "../../components/task-board/squadStats";
import Avatar from "../../components/ui/Avatar";

const Tile = ({ label, value, danger }) => (
  <div className="min-w-0 p-3" style={{ background: "var(--panel2)", borderRadius: 12 }}>
    <p className="text-[11px] uppercase tracking-wide text-faint whitespace-nowrap truncate">{label}</p>
    <p
      className="font-display mt-1"
      style={{ fontSize: 20, fontVariantNumeric: "tabular-nums", color: danger ? "var(--red)" : "var(--text)" }}
    >
      {value}
    </p>
  </div>
);

const SquadCard = ({ squad, tasks, sprints }) => {
  const { current, currentStats, allStats } = useMemo(() => squadOverview(tasks, sprints), [tasks, sprints]);
  const members = squad.members.filter((m) => m.employee);
  const leads = members.filter((m) => m.role === "SQUAD_LEAD");

  return (
    <Link
      to={`/task-board/squads/${squad._id}`}
      className="block min-w-0 transition-[transform,border-color] duration-200 hover:-translate-y-[3px] hover:!border-[color:var(--accent-line)]"
      style={{
        background: "var(--panel)",
        border: "1px solid var(--line)",
        borderRadius: 18,
        padding: 22,
        backdropFilter: "blur(20px)",
        boxShadow: "var(--card-shadow)",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display font-semibold text-ink truncate" style={{ fontSize: 22 }}>{squad.name}</h2>
          <p className="mt-1 flex items-center gap-1.5 text-[13px] text-muted min-w-0">
            <Crown className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--accent)" }} />
            <span className="truncate">{leads.length ? leads.map((l) => l.employee.fullNameEn).join(", ") : "No lead"}</span>
            <span className="text-faint whitespace-nowrap">· Updated {formatDate(squad.updatedAt)}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="flex">
            {members.slice(0, 5).map((m, i) => (
              <span
                key={m.employee._id}
                className="rounded-full"
                style={{ marginLeft: i ? -8 : 0, boxShadow: "0 0 0 2px var(--panel-solid)" }}
              >
                <Avatar
                  src={m.employee.photo}
                  name={m.employee.fullNameEn}
                  size="sm"
                  preview={false}
                  className="!w-[30px] !h-[30px] !text-[10px]"
                  title={m.employee.fullNameEn}
                />
              </span>
            ))}
          </span>
          <span className="text-[13px] text-muted whitespace-nowrap">{members.length}</span>
        </div>
      </div>

      <p className="mt-3 text-[13.5px] text-muted" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", minHeight: "2.7em" }}>
        {squad.description || "No description"}
      </p>

      <div className="mt-4">
        <div className="flex items-center justify-between gap-3 text-[12.5px] mb-2">
          <span className="text-muted whitespace-nowrap">{current ? `${current.name} · Current` : "No current sprint"}</span>
          {currentStats && (
            <span className="text-muted whitespace-nowrap">
              {currentStats.done} / {currentStats.tasks} done{" "}
              <b style={{ color: "var(--accent-ink)" }}>{currentStats.pct}%</b>
            </span>
          )}
        </div>
        <StatusBar byStatus={currentStats?.byStatus || {}} total={currentStats?.tasks || 0} />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <Tile label="Open this sprint" value={currentStats?.open ?? 0} />
        <Tile label="Overdue" value={currentStats?.overdue ?? 0} danger={currentStats?.overdue > 0} />
        <Tile label="All sprints" value={allStats.tasks} />
      </div>
    </Link>
  );
};

const Squads = () => {
  const { auth } = useAuth();
  const admin = isAdmin(auth?.employee);
  const [squads, setSquads] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sq, sp, tk] = await Promise.all([squadService.list(), sprintService.list(), boardTaskService.list()]);
      setSquads(sq.data);
      setSprints(sp.data);
      setTasks(tk.data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load squads"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tasksBySquad = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      const id = t.squad && typeof t.squad === "object" ? t.squad._id : t.squad;
      if (id) map.set(id, [...(map.get(id) || []), t]);
    }
    return map;
  }, [tasks]);

  return (
    <>
      <PageWrapper
        title={<span style={{ fontSize: 34, fontWeight: 600, letterSpacing: "-.035em" }}>Squads</span>}
        subtitle={`All squads, their members and how the current sprint is going.`}
        actions={
          admin && (
            <Button size="sm" onClick={() => setCreating(true)} className="whitespace-nowrap shrink-0">
              <Plus className="w-4 h-4" /> New squad
            </Button>
          )
        }
      >
        {error ? (
          <ErrorState message={error} onRetry={load} />
        ) : loading ? (
          <SkeletonRows rows={4} />
        ) : squads.length === 0 ? (
          <EmptyState
            icon={Users}
            title={admin ? "No squads yet" : "You are not in a squad"}
            message={admin ? "Create a squad, then add members as Squad Lead or Member." : "Ask an admin to add you to a squad."}
          />
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 440px), 1fr))" }}>
            {squads.map((s) => (
              <SquadCard key={s._id} squad={s} tasks={tasksBySquad.get(s._id) || []} sprints={sprints} />
            ))}
          </div>
        )}
      </PageWrapper>
      <SquadFormModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          load();
        }}
      />
    </>
  );
};

export default Squads;
